/**
 * API client for communicating with Azure Functions backend.
 * Replaces direct Supabase client calls.
 */

import { globalQueue } from './requestQueue.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

type TokenProvider = (opts?: { skipCache?: boolean; organizationId?: string }) => Promise<string | null>;
let getTokenFn: TokenProvider | null = null;

/**
 * Register the Clerk getToken function. Called once from ClerkProvider setup.
 * Accepts `{ skipCache }` so callers can force a fresh token — needed right
 * after creating/switching the active organization (see apiFetch).
 */
export function setTokenProvider(fn: TokenProvider) {
  getTokenFn = fn;
}

/**
 * Authenticated fetch wrapper that automatically includes the Clerk JWT.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options?: RequestInit & { skipAuth?: boolean; skipQueue?: boolean },
): Promise<T> {
  const { skipAuth, skipQueue, ...fetchOptions } = options || {};
  const method = (fetchOptions.method || 'GET').toUpperCase();

  const run = async (skipTokenCache: boolean): Promise<Response> => {
    const isFormData =
      typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(fetchOptions.headers as Record<string, string>),
    };

    if (!skipAuth && getTokenFn) {
      const token = await getTokenFn(skipTokenCache ? { skipCache: true } : undefined);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const doFetch = () =>
      fetch(`${API_BASE_URL}${path}`, {
        ...fetchOptions,
        headers,
        signal: fetchOptions.signal,
      });

    return skipQueue ? doFetch() : globalQueue.enqueue(path, method, doFetch);
  };

  let response = await run(false);

  // Clerk caches session tokens briefly. After a Clerk environment/key cutover
  // (and occasionally around session renewal) that cached token can be rejected
  // even though the signed-in session is healthy. Force one fresh token before
  // surfacing a 401 to the UI.
  if (response.status === 401 && !skipAuth && getTokenFn) {
    response = await run(true);
  }

  // The Clerk session token caches for ~60s, so right after creating or
  // switching the active organization the cached token still has no org_id and
  // org-scoped endpoints 400. Refresh the token (skipCache) once and retry.
  if (response.status === 400 && !skipAuth && getTokenFn) {
    const peek = await response.clone().json().catch(() => null);
    const msg = typeof peek?.error === 'string' ? peek.error.toLowerCase() : '';
    if (msg.includes('active organization')) {
      response = await run(true);
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const error = new ApiError(body.error || response.statusText, response.status, body.code, body.requestId);
    if (body.requestId) {
      lastRequestId = body.requestId;
    }
    throw error;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Fetch a binary response (e.g. a server-rendered PDF) as a Blob. Mirrors
 * apiFetch's auth + post-org-switch token refresh, but returns the raw bytes
 * instead of parsing JSON. Errors still surface as ApiError (parsed from a JSON
 * error body when present).
 */
export async function apiFetchBlob(
  path: string,
  options?: { skipAuth?: boolean; signal?: AbortSignal },
): Promise<Blob> {
  const { skipAuth, signal } = options || {};

  const run = async (skipTokenCache: boolean): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (!skipAuth && getTokenFn) {
      const token = await getTokenFn(skipTokenCache ? { skipCache: true } : undefined);
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    return fetch(`${API_BASE_URL}${path}`, { method: 'GET', headers, signal });
  };

  let response = await run(false);
  // Mirror apiFetch's stale-token recovery for authenticated downloads.
  if (response.status === 401 && !skipAuth && getTokenFn) {
    response = await run(true);
  }
  // Same stale-org-token retry as apiFetch (see there for the why).
  if (response.status === 400 && !skipAuth && getTokenFn) {
    const peek = await response.clone().json().catch(() => null);
    const msg = typeof peek?.error === 'string' ? peek.error.toLowerCase() : '';
    if (msg.includes('active organization')) response = await run(true);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    if (body.requestId) lastRequestId = body.requestId;
    throw new ApiError(body.error || response.statusText, response.status, body.code, body.requestId);
  }
  return response.blob();
}

/** Trigger a browser download of a Blob under the given filename. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Most recent requestId returned by the API for a 500 error. */
export let lastRequestId: string | undefined;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Validated request helpers — validate body with a Zod schema before sending.
import type { ZodType } from 'zod';

export async function validatedPost<TSchema extends ZodType, TResponse = unknown>(
  path: string,
  schema: TSchema,
  body: unknown,
  options?: RequestInit,
): Promise<TResponse> {
  const parsed = schema.parse(body);
  return api.post<TResponse>(path, parsed, options);
}

export async function validatedPatch<TSchema extends ZodType, TResponse = unknown>(
  path: string,
  schema: TSchema,
  body: unknown,
  options?: RequestInit,
): Promise<TResponse> {
  const parsed = schema.parse(body);
  return api.patch<TResponse>(path, parsed, options);
}

/**
 * Validated GET — validates path parameters with a Zod schema before
 * interpolating them into the URL template.
 *
 * Usage:
 *   validatedGet('/analyses/:id', UuidParamSchema, { id: someId })
 */
export async function validatedGet<TSchema extends ZodType, TResponse = unknown>(
  pathTemplate: string,
  schema: TSchema,
  params: Record<string, string>,
  options?: RequestInit,
): Promise<TResponse> {
  const parsed = schema.parse(params) as Record<string, string>;
  const path = pathTemplate.replace(/:([a-zA-Z_]+)/g, (_, key) => {
    if (!(key in parsed)) throw new Error(`Missing path parameter: ${key}`);
    return encodeURIComponent(parsed[key]);
  });
  return api.get<TResponse>(path, options);
}

export function apiPostForm<T = unknown>(
  path: string,
  body: FormData,
  options?: RequestInit,
): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'POST', body });
}

// Convenience methods
export const api = {
  get: <T = unknown>(path: string, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  put: <T = unknown>(path: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  patch: <T = unknown>(path: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),

  delete: <T = unknown>(path: string, options?: RequestInit) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
